import type {
  ListTeamMembersFn,
  GetCurrentMemberFn,
  SearchMembersFn,
} from '@/features/iam'
import type { AlertFactory } from '@/features/shell'
import { type Member } from '@/features/iam/members/Member'
import { ModalViewModel } from '@/infra/ModalViewModel'
import { type Job, type CollaboratorType } from '@/features/jobs/Job'
import { type JobStore } from '@/features/jobs/JobStore'
import { action, computed, observable } from 'mobx'
import { task } from 'mobx-task'
import { CursorPaged, type CursorPageQuery } from '@/infra/paging'
import { Id } from '@/infra/id'
import { mustHaveMaxLength } from '@/utils/validx-util'
import { required, validationContext } from 'validx'
import { type ProviderStore } from '@/features/jobs/ProviderStore'

/**
 * View Model for the Add Collaborators as Admin dialog.
 */
export class AddCollaboratorsAsAdminDialogViewModel {
  /**
   * Max reason length.
   */
  static readonly MAX_REASON_LENGTH = 100

  /**
   * The dialog's modal state.
   */
  readonly modal = new ModalViewModel({ onClose: () => this.reset() })

  /**
   * Collaborator to add.
   */
  @observable accessor collaborator: Member | null | undefined = undefined

  /**
   * The type of collaborator we are adding to the job
   */
  @observable accessor collaboratorType: CollaboratorType = 'Pro'

  /**
   * Disables submit button.
   */
  @observable accessor disabled = true

  /**
   * The reason for adding the collaborator.
   */
  @observable accessor reason = ''

  /**
   * Whether the Pro selector lists all Pros instead of only qualified ones.
   */
  @observable accessor canDisplayAllPros = false

  /**
   * Validation context for View Model.
   */
  readonly validation = validationContext(this, {
    collaborator: [required('Collaborator is required')],
    reason: [
      mustHaveMaxLength(
        AddCollaboratorsAsAdminDialogViewModel.MAX_REASON_LENGTH,
        `Reason must be less than ${AddCollaboratorsAsAdminDialogViewModel.MAX_REASON_LENGTH} characters.`,
      ),
    ],
  })

  constructor(
    private readonly _job: Job,
    private readonly _canAddSupportingClientAsAdmin: () => boolean,
    private readonly _canAddChampionProviderAsAdmin: () => boolean,
    private readonly _canAddSupportingProviderAsAdmin: () => boolean,
    private readonly _canRequestChampionProviderAsAdmin: () => boolean,
    private readonly _jobStore: JobStore,
    private readonly _findMembers: SearchMembersFn,
    private readonly _listTeamMembers: ListTeamMembersFn,
    private readonly _providerStore: ProviderStore,
    private readonly _newAlert: AlertFactory,
    private readonly _getCurrentMember: GetCurrentMemberFn,
  ) {
    if (this.isDraftJob || this.canOnlyAddClient) {
      this.setAddingClient()
    }
  }

  /**
   * Helper text for reason input field.
   */
  @computed
  get reasonHelpText() {
    if (this.isAddingClient) {
      return 'Why are you adding another Client? This text is public and can be seen by the Client and all Pros.'
    }

    return this.modalType === 'champion'
      ? 'This text is public and can be seen by the Client and all Pros.'
      : 'Why are you adding another Pro? This text is public and can be seen by the Client and all Pros.'
  }

  /**
   * Helper text for the Pro selection field.
   */
  @computed
  get proSelectionHelpText() {
    return this.modalType === 'requestingChampion'
      ? 'The selected Pro will receive a request and will be added to the job upon approval.'
      : undefined
  }

  /**
   * Whether the Pro selector lists only qualified Pros.
   */
  @computed
  get canDisplayQualifiedPros() {
    return !this.canDisplayAllPros
  }

  /**
   * Whether the dialog adds a Pro.
   */
  @computed
  get isAddingPro() {
    return this.collaboratorType === 'Pro'
  }

  /**
   * Whether the dialog adds a Client.
   */
  @computed
  get isAddingClient() {
    return this.collaboratorType === 'Client'
  }

  /**
   * Whether the job belongs to a client team.
   */
  @computed
  get isClientTeamJob() {
    return this._job.isClientTeamJob
  }

  /**
   * Whether the job is still a draft.
   */
  @computed
  get isDraftJob() {
    return this._job.isDraft
  }

  /**
   * Whether the current member may add Clients but not Pros.
   */
  @computed
  get canOnlyAddClient() {
    return (
      !this._getCurrentMember(this._currentMemberWorkspaceId).permissions
        .adminUserList &&
      this._getCurrentMember(this._currentMemberWorkspaceId).permissions
        .adminClientUserList
    )
  }

  /**
   * Modal type when adding a Pro.
   */
  @computed
  get modalType() {
    if (this._job.status === 'Unclaimed') {
      return 'champion'
    }

    if (this._job.status === 'InfoGathering') {
      return 'requestingChampion'
    }

    return 'supporting'
  }

  /**
   * Reason input characters remaining text label.
   */
  @computed
  get reasonCharsRemaining() {
    return this.reason
      ? AddCollaboratorsAsAdminDialogViewModel.MAX_REASON_LENGTH -
          this.reason.length
      : AddCollaboratorsAsAdminDialogViewModel.MAX_REASON_LENGTH
  }

  /**
   * Shows the Add Pro as Admin Dialog.
   */
  @action.bound
  show() {
    if (this.modal.showing) {
      return
    }

    this.modal.show()
  }

  /**
   * Sets dropdown to show all pros.
   */
  @action.bound
  setDisplayAllPros() {
    this.canDisplayAllPros = true
  }

  /**
   * Sets dropdown to show only qualified pros.
   */
  @action.bound
  setDisplayQualifiedPros() {
    this.canDisplayAllPros = false
  }

  /**
   * Sets a collaborator to add.
   */
  @action.bound
  setCollaborator(collaborator?: Member | null) {
    if (this.collaborator === collaborator) {
      return
    }
    this.collaborator = collaborator
    this.disabled = !this.validate()
  }

  /**
   * Clears the fields of the View Model.
   */
  @action.bound
  reset() {
    this.collaborator = undefined
    this.reason = ''
    this.disabled = true
    this.canDisplayAllPros = false

    this.submit.reset()
    this.validation.reset()
  }

  /**
   * Indicates if the View Model is valid.
   */
  @action.bound
  validate() {
    return this.validation.reset().validate().isValid
  }

  /**
   * Sets the collaborator type we are adding to Pro.
   */
  @action.bound
  setAddingPro() {
    this.collaborator = undefined
    this.collaboratorType = 'Pro'
  }

  /**
   * Sets the collaborator type we are adding to Client.
   */
  @action.bound
  setAddingClient() {
    this.collaborator = undefined
    this.collaboratorType = 'Client'
  }

  /**
   * Submits the new collaborator depending on the type and role.
   */
  submit = task.resolved(async () => {
    const name = this.collaborator?.displayName
    const jobName = this._job.displayName
    const userPublicId = this.collaborator?.user.publicId

    if (this.isAddingClient) {
      return this._addAsAdmin({
        allowed: this._canAddSupportingClientAsAdmin(),
        pending: ['Adding Client', `Adding ${name} to ${jobName}...`],
        success: `${name} was added to ${jobName}.`,
        add: () =>
          this._jobStore.addSupportingClientAsAdmin({
            jobId: this._job.id,
            clientUserPublicId: userPublicId,
            reason: this.reason,
          }),
      })
    }

    if (this.modalType === 'champion') {
      return this._addAsAdmin({
        allowed: this._canAddChampionProviderAsAdmin(),
        pending: ['Adding Provider', `Adding ${name} to ${jobName}...`],
        success: `${name} was added to ${jobName}.`,
        add: () =>
          this._jobStore.addChampionProviderAsAdmin({
            jobId: this._job.id,
            providerUserPublicId: userPublicId,
            reason: this.reason,
          }),
      })
    }

    if (this.modalType === 'supporting') {
      return this._addAsAdmin({
        allowed: this._canAddSupportingProviderAsAdmin(),
        pending: ['Adding Provider', `Adding ${name} to ${jobName}...`],
        success: `${name} was added to ${jobName}.`,
        add: () =>
          this._jobStore.addSupportingProviderAsAdmin({
            jobId: this._job.id,
            providerUserPublicId: userPublicId,
            reason: this.reason,
          }),
      })
    }

    return this._addAsAdmin({
      allowed: this._canRequestChampionProviderAsAdmin(),
      pending: ['Requesting Provider', `Requesting ${name} to ${jobName}...`],
      keepPending: true,
      success: `${name} was requested to ${jobName}.`,
      add: () =>
        this._jobStore.requestChampionProviderAsAdmin(
          this._job.id,
          Id.assert(userPublicId),
        ),
    })
  })

  /**
   * Fetches collaborators.
   */
  fetchWorkspaceMembers = async (
    searchText: string,
    params: Partial<CursorPageQuery>,
    signal?: AbortSignal,
  ): Promise<CursorPaged<Member>> => {
    const collaborators = this._job.collaborators
    const paged = await this._findMembers(
      {
        after: params.after,
        limit: params.limit,
        workspaceId: this._currentMemberWorkspaceId,
        searchText,
        userTypes: this.canOnlyAddClient ? ['User'] : undefined,
      },
      signal,
    )

    return CursorPaged.map(paged, (data) =>
      data.filter((m) => {
        const invalidCandidate =
          collaborators.some((s) => s.member === m) ||
          m.user.id === 'SYSTEM_ROBOT'
        return !invalidCandidate
      }),
    )
  }

  /**
   * Fetches qualified pros.
   */
  fetchQualifiedPros = (
    searchText: string,
    query: Partial<CursorPageQuery>,
  ): Promise<CursorPaged<Member | null>> =>
    this._providerStore.listQualifiedProviders({
      job: this._job,
      searchText,
      after: query.after ?? undefined,
    })

  /**
   * Fetches team members.
   */
  fetchTeamMembers = async (
    _searchText: string,
    params: Partial<CursorPageQuery>,
  ): Promise<CursorPaged<Member | null>> => {
    if (!this._job.ownerTeam) {
      return CursorPaged.empty()
    }

    const collaborators = this._job.collaborators
    const paged = await this._listTeamMembers({
      after: params.after,
      teamId: this._job.ownerTeam.id,
    })

    return CursorPaged.map(paged, (data) =>
      data
        .filter((m) => {
          const invalidCandidate = collaborators.some(
            (s) => s.member === m.member,
          )
          return !invalidCandidate
        })
        .map((m) => m.member),
    )
  }

  private async _addAsAdmin(options: AddAsAdminOptions) {
    if (!options.allowed) {
      this._newAlert()
        .withState('error')
        .saying('You do not have permission to perform this action.')
        .autoDismiss()
        .show()
      return
    }

    if (!this.validate()) {
      return
    }

    const alert = this._newAlert()
      .withState('pending')
      .saying(...options.pending)
    if (!options.keepPending) {
      alert.autoDismiss()
    }
    alert.show()

    try {
      await options.add()
    } catch (err) {
      alert.dismiss()
      throw err
    }

    alert.withState('success').saying('Success!', options.success).autoDismiss()
    this.modal.close()
  }

  @computed
  private get _currentMemberWorkspaceId() {
    return Id.assert(
      this._getCurrentMember(this._job.workspace.id).workspace.id,
    )
  }
}

interface AddAsAdminOptions {
  allowed: boolean
  pending: [title: string, description: string]
  keepPending?: boolean
  success: string
  add: () => Promise<unknown>
}
